/**
 * "Manual Actions" bucket card for the Action required group.
 * The card row shows a warning icon, label, total count (always visible, even at 0) and a chevron.
 * Clicking it opens a floating popup of sections; picking sections adds removable chips to the card.
 * Manual actions are mutually exclusive with the other filters and apply immediately on change.
 */
import { observer } from "mobx-react";
import "./rma-list-manual-actions-bucket.scss";
import useDashboardTranslation from "../../../../../dashboard-translation/hooks/dashboard-translation-hook";
import { MANUAL_ACTION_TYPE } from "../../../../../services/api/rma-list-service";
import RmaListFiltersStore from "../../../rma-list-page/stores/rma-list-filters-store";
import RmaListManualActionsPopup from "../rma-list-manual-actions-popup/rma-list-manual-actions-popup";
import RmaListManualActionsBucketCardRow from "./rma-list-manual-actions-bucket-card-row/rma-list-manual-actions-bucket-card-row";
import RmaListManualActionsBucketChips from "./rma-list-manual-actions-bucket-chips/rma-list-manual-actions-bucket-chips";
import useBucketPopup from "../../hooks/use-bucket-popup";

interface IProps {
    rmaListFiltersStore: RmaListFiltersStore;
    disabled: boolean;
    onApply: () => void;
}

/** Maps a manual-action bucket value to its dashboard-translation key (scope: dashboard-returns-page). */
const MANUAL_ACTION_LABEL_KEY: Record<string, string> = {
    [MANUAL_ACTION_TYPE.APPROVE]: "MANUAL_ACTION_APPROVE",
    [MANUAL_ACTION_TYPE.MANAGER_APPROVAL]: "MANUAL_ACTION_MANAGER_APPROVAL",
    [MANUAL_ACTION_TYPE.RECEIVE]: "MANUAL_ACTION_RECEIVE",
    [MANUAL_ACTION_TYPE.REFUND]: "MANUAL_ACTION_REFUND",
    [MANUAL_ACTION_TYPE.RELEASE_EXCHANGE]: "MANUAL_ACTION_RELEASE_EXCHANGE",
    [MANUAL_ACTION_TYPE.VALIDATE]: "MANUAL_ACTION_VALIDATE",
    [MANUAL_ACTION_TYPE.RESTOCK]: "MANUAL_ACTION_RESTOCK",
    [MANUAL_ACTION_TYPE.MARK_DONE]: "MANUAL_ACTION_MARK_DONE",
};

const RmaListManualActionsBucket: React.FC<IProps> = observer(({ rmaListFiltersStore, disabled, onApply }) => {
    const { getTranslation } = useDashboardTranslation();
    const scope = "dashboard-returns-page";
    const { isOpen, refs, floatingStyles, getReferenceProps, getFloatingProps } = useBucketPopup(disabled);

    const selected = rmaListFiltersStore.selectedManualActions;
    const hasSelection = selected.length > 0;

    const getLabel = (value: string) => getTranslation(MANUAL_ACTION_LABEL_KEY[value] ?? value, scope);
    const getCount = (value: string) =>
        rmaListFiltersStore.manualActionOptions.find((option) => option.value === value)?.count ?? 0;

    const toggleAction = (value: string) => {
        rmaListFiltersStore.toggleManualAction(value);
        onApply();
    };

    const toggleAllActions = () => {
        rmaListFiltersStore.toggleAllManualActions();
        onApply();
    };

    const cardActive = isOpen || hasSelection;
    // Empty bucket (no open RMAs needing a manual action) stays visible but dimmed, unless the merchant
    // is actively interacting with it (open / has a selection).
    const isEmpty = rmaListFiltersStore.manualActionsTotal === 0 && !cardActive;

    return (
        <div className="rma-list-manual-actions-bucket">
            <div className={`rma-list-manual-actions-bucket__card${cardActive ? " active" : ""}${isEmpty ? " empty" : ""}${disabled ? " disabled" : ""}`}>
                <RmaListManualActionsBucketCardRow
                    label={getTranslation("MANUAL_ACTIONS", scope)}
                    total={rmaListFiltersStore.manualActionsTotal}
                    open={isOpen}
                    disabled={disabled}
                    triggerRef={refs.setReference}
                    triggerProps={getReferenceProps()}
                />

                {hasSelection && (
                    <RmaListManualActionsBucketChips
                        selected={selected}
                        getLabel={getLabel}
                        getCount={getCount}
                        disabled={disabled}
                        onRemove={toggleAction}
                    />
                )}
            </div>

            {isOpen && (
                <div
                    ref={refs.setFloating}
                    style={floatingStyles}
                    className="rma-list-manual-actions-bucket__popup"
                    {...getFloatingProps()}
                >
                    <RmaListManualActionsPopup
                        rmaListFiltersStore={rmaListFiltersStore}
                        getLabel={getLabel}
                        onToggle={toggleAction}
                        onToggleAll={toggleAllActions}
                    />
                </div>
            )}
        </div>
    );
});

export default RmaListManualActionsBucket;
